<script setup>
import { ref, computed, watch, onBeforeMount, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import moment from 'moment-timezone'
import { storeToRefs } from 'pinia'
import { until } from '@vueuse/core'
import { useRoute, useRouter } from 'vue-router'
import { numberWithCommas } from '@/plugins/misc'
import {
  useUsersStore,
  useReservationsStore,
  useUserGroupsStore,
  useRestaurantsStore
} from '@/stores'
import googleAttribution from '@/assets/images/ui/google_on_white.png'

// Store initialization
const { t } = useI18n()
const usersStore = useUsersStore()
const userGroupsStore = useUserGroupsStore()
const reservationsStore = useReservationsStore()
const restaurantsStore = useRestaurantsStore()
const route = useRoute()
const router = useRouter()

// Use storeToRefs for restaurant store refs that need to be writable
const { viewingDetailsRestaurant, viewingDetailsRestaurantTab } = storeToRefs(restaurantsStore)

// Local refs
const tab = ref('Bookings')
const pendingReservationType = ref('general')
const handlingReservationQuery = ref(false)

// Store refs
const myUser = computed(() => usersStore.myUser)
const isShowingPreferredRequestInfoDialog = computed(() => usersStore.isShowingPreferredRequestInfoDialog)

const myReservations = computed(() => reservationsStore.myReservations)
const fetching = computed(() => reservationsStore.fetching)

const restaurants = computed(() => restaurantsStore.restaurants)

// View state
const showingGroups = ref({
  Active: false,
  Upcoming: false,
  Experienced: false,
  Other: false
})

const tabsSetInitially = ref(false)

// Computed properties
const bookingsCount = computed(() =>
  activeReservations.value.length +
  completeReservations.value.length +
  NAReservations.value.length +
  directReservations.value.length
)

// Computed properties for reservation filtering
const directReservations = computed(() => {
  if (!myReservations.value) return []

  const reservations = myReservations.value.filter(el => el?.directBooking)
  
  return reservations.sort((a, b) => moment(b.sortDateTime).diff(moment(a.sortDateTime)))
})

const acceptedReservations = computed(() => {
  if (!myReservations.value) return []

  return myReservations.value.filter(el =>
    !el?.directBooking &&
    el.userPermissions.some(e => e?.email === myUser?.value?.email) &&
    el.status === "Accepted" &&
    (el.date || el.preferredTime || el.partySize || el.preferredRestaurant)
  ).reverse()
})

const completeReservations = computed(() => {
  if (!myReservations.value) return []

  const reservations = myReservations.value.filter(el => {
    if (el.reservationType != 'instant' && el.alternateRestaurants?.length === 0 && !el.preferredRestaurant) {

      return false
    }
    
    return !el?.directBooking &&
      el.userPermissions.some(e => e?.email === myUser?.value?.email) &&
      ((el.status === "Accepted" || el.status === "Experienced") && moment(el.sortDateTime).isBefore(moment())) &&
      !(el.status === "Draft" ||
        el.status === 'Awaiting Response from Restaurant' ||
        el.status === 'In Review' ||
        el.status === 'Member Action' ||
        el.status === 'Pending RM Review')
  })

  return reservations.sort((a, b) => moment(b.sortDateTime).diff(moment(a.sortDateTime)))
})

const NAReservations = computed(() => {
  if (!myReservations.value) return []

  const reservations = myReservations.value.filter(el =>
    !el?.directBooking &&
    el.userPermissions.some(e => e?.email === myUser?.value?.email) &&
    (["Canceling", "Rescinding", "Not Available", "No Response", "Declined", "Canceled", "Rescinded"].includes(el.status)
      // RM-400: an inquiry the member never marked as booked leaves Active after its date, still Submitted.
      || (el.reservationType === 'private' && el.status === 'Submitted' && (el.date || '') < moment().format('YYYY-MM-DD')))
  )

  return reservations.sort((a, b) => moment(b.sortDateTime).diff(moment(a.sortDateTime)))
})

const reservationGroupings = computed(() => {
  const groups = [
    { id: 'Active', name: t('bookings.groups.active'), items: activeReservations.value, emptyState: true }
  ]

  if (completeReservations.value.length > 0) {
    groups.push({ id: 'Experienced', name: t('bookings.groups.experienced'), items: completeReservations.value, emptyState: false })
  }

  if (NAReservations.value.length > 0) {
    groups.push({ id: 'CanceledOrNA', name: t('bookings.groups.canceledOrNA'), items: NAReservations.value, emptyState: false })
  }

  return groups
})

const pastAndCurrentAcceptedBookings = computed(() => {
  const rmAndDirect = [...acceptedReservations.value, ...directReservations.value]
  
  return rmAndDirect.sort((a, b) => moment(b.date).diff(moment(a.date)))
})

const activeReservations = computed(() => {
  var returnArray = []

  if (myReservations.value) {

    returnArray = myReservations.value.filter(function (el) {
      // Terminal reservations (Canceled/Rescinded/Declined/etc.) never show in
      // Active — they move straight to Canceled/N-A (Kapil, 7/23; previously
      // they lingered here for 24h after the status change).
      return !el?.directBooking
        && el.userPermissions.some(e => e?.email === myUser?.value?.email)
        && ((el.status === "Draft" || el.status == 'Awaiting Response from Restaurant' || el.status == 'In Review' || el.status == 'Member Action' || el.status == 'Pending RM Review')
          // RM-400: a group/private dining inquiry is Submitted (or "Booked, pending RM approval")
          // and stays here until its event date (Kapil, 10/12).
          || (el.reservationType === 'private' && (el.status === 'Submitted' || el.status === 'Approve?') && (el.date || '') >= moment().format('YYYY-MM-DD'))
          || (el.userPermissions.some(e => e?.email === myUser?.value?.email) && (el.status == "Accepted" && moment(el.sortDateTime).isAfter(moment())))
        )
        && (el.date || el.preferredTime || el.partySize || el.preferredRestaurant)
    })
    returnArray = returnArray.slice().reverse()
  }
  returnArray.sort(function (a, b) {
    if (a.sortDateTime < b.sortDateTime) {
      return 1
    }
    if (a.sortDateTime > b.sortDateTime) {
      return -1
    }
    
    return 0
  })

  // Sort by status in the order: Awaiting Response from Restaurant, In Review, Member Action, Pending RM Review, then others, Draft last
  returnArray.sort(function (a, b) {
    if (a.status === b.status) {
      return 0
    }
    if (a.status === 'Awaiting Response from Restaurant') {
      return -1
    }
    if (b.status === 'Awaiting Response from Restaurant') {
      return 1
    }
    if (a.status === 'In Review') {
      return -1
    }
    if (b.status === 'In Review') {
      return 1
    }
    if (a.status === 'Member Action') {
      return -1
    }
    if (b.status === 'Member Action') {
      return 1
    }
    if (a.status === 'Pending RM Review') {
      return -1
    }
    if (b.status === 'Pending RM Review') {
      return 1
    }
    if (a.status === 'Draft') {
      return 1
    }
    if (b.status === 'Draft') {
      return -1
    }

    return 0
  })

  return returnArray
})

// Methods
const newDraft = async (market, restaurant, reservationType = 'general') => {
  try {
    await usersStore.showLoadingOverlay()
    pendingReservationType.value = reservationType
    if (!myUser.value) {
      await usersStore.loginNewDummyUser()
    }
    await usersStore.startNewReservation({ restaurant, market, reservationType })
    pendingReservationType.value = 'general'
  } catch (error) {
    pendingReservationType.value = 'general'
    console.error('Error creating new draft:', error)
  } finally {
    await usersStore.hideLoadingOverlay()
  }
}

const openPrivateDiningFromQuery = async () => {
  if (handlingReservationQuery.value || route.query.startPrivateDining !== '1')
    return

  handlingReservationQuery.value = true
  try {
    await newDraft('', '', 'private')
  } finally {
    const nextQuery = { ...route.query }
    delete nextQuery.startPrivateDining
    await router.replace({ query: nextQuery })
    handlingReservationQuery.value = false
  }
}

// RM-205: the agent's external channels (email/SMS) hand off submission via a
// secure link that lands here as /bookings?submitRequest=<reservationId> —
// open the review dialog at step 7a, same as the in-chat CTA button.
const openSubmitRequestFromQuery = async () => {
  const reservationId = route.query.submitRequest
  if (!reservationId || handlingReservationQuery.value)
    return

  handlingReservationQuery.value = true
  try {
    await reservationsStore.openReservationDialogAtStep(reservationId, '7a')
  } finally {
    const nextQuery = { ...route.query }
    delete nextQuery.submitRequest
    await router.replace({ query: nextQuery })
    handlingReservationQuery.value = false
  }
}

// RM-359: the follow-up on an unfinished request links here as
// /bookings?openRequest=<reservationId> — the request opens exactly as tapping
// it on this page does, with what it has filled in. (A request ready for
// review gets ?submitRequest= instead.) It opens once this page has the
// restaurant list, as a tap would, so the form finds the request's restaurant
// straight away (up to 8 seconds; then it opens anyway).
const openRequestFromQuery = async () => {
  const reservationId = route.query.openRequest
  if (!reservationId || handlingReservationQuery.value)
    return

  handlingReservationQuery.value = true
  try {
    await until(() => restaurantsStore.hasFetchedRestaurantList).toBe(true, { timeout: 8000 })
    await reservationsStore.openRequest(reservationId)
  } finally {
    const nextQuery = { ...route.query }
    delete nextQuery.openRequest
    await router.replace({ query: nextQuery })
    handlingReservationQuery.value = false
  }
}

const showRestaurantDetails = (marketName, restaurantName) => {
  const restaurant = restaurants.value.find(r =>
    r.name === restaurantName && r.market === marketName
  )

  if (restaurant) {
    viewingDetailsRestaurantTab.value = "Details"
    viewingDetailsRestaurant.value = restaurant
  }
}

// Fetch friends-favorited data for restaurants in bookings (non-blocking)
const fetchFriendsData = () => {
  if (!myUser.value || myUser.value.userLevel === 'dummy') return
  const favIds = myUser.value?.favoritedRestaurants
  if (favIds?.length) {
    restaurantsStore.fetchFriendsFavoritedBatch(favIds)
  }
}

// Lifecycle hooks
onBeforeMount(() => {
  console.log('Navigating to bookings page')
  if (myUser.value) reservationsStore.getMyReservations()
  reservationsStore.setupMyReservationsListeners()
  restaurantsStore.getRestaurants()
  openPrivateDiningFromQuery()
  openSubmitRequestFromQuery()
  openRequestFromQuery()

  // Fetch friends data once restaurants are loaded
  if (restaurants.value?.length > 0) {
    fetchFriendsData()
  } else {
    const stopWatch = watch(restaurants, newVal => {
      if (newVal?.length > 0) {
        fetchFriendsData()
        stopWatch()
      }
    })
  }
})

onBeforeUnmount(() => {
  console.log('Navigating away from bookings page')
  reservationsStore.removeMyReservationsListeners()
})

// Watchers
watch([activeReservations, completeReservations], ([newActive, newComplete]) => {
  if (!tabsSetInitially.value && (newActive.length > 0 || newComplete.length > 0)) {
    tabsSetInitially.value = true

    // RM AI links here after attaching a receipt (/bookings?tab=Experienced), so the
    // member lands on the booking their receipt is on (Founder review, 2026-09-17).
    const requested = String(route.query.tab || '')
    if (requested && Object.prototype.hasOwnProperty.call(showingGroups.value, requested)) {
      showingGroups.value[requested] = true

      return
    }
    showingGroups.value.Active = newActive.length > 0
    showingGroups.value.Experienced = !newActive.length && newComplete.length > 0
  }
})

// Fetch insights when switching to Insights tab
watch(tab, newTab => {
  if (newTab === 'Insights' && !reservationsStore.insights && !reservationsStore.insightsLoading) {
    reservationsStore.fetchInsights()
  }
})

watch(() => route.query.startPrivateDining, startPrivateDining => {
  if (startPrivateDining === '1') {
    openPrivateDiningFromQuery()
  }
})

watch(() => route.query.submitRequest, submitRequest => {
  if (submitRequest) {
    openSubmitRequestFromQuery()
  }
})

watch(() => route.query.openRequest, openRequest => {
  if (openRequest) {
    openRequestFromQuery()
  }
})
</script>

<template>
  <div>
    <!-- Main Content -->
    <div class="main-wrapper">
      <div class="max-content-width position-relative">
        <!-- Booking Button -->
        <div class="book-now-holder">
          <div class="max-content-width header-new-reservation-button-sticky-holder mt-2">
            <div class="d-flex flex-row align-center justify-center my-2">
              <span
                class="ml-1 mr-2 mt-3"
                @click="newDraft"
              >
                <svg
                  :width="$vuetify.display.smAndDown ? 16 : 24"
                  :height="$vuetify.display.smAndDown ? 16 : 24"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <ellipse
                    cx="8"
                    cy="8"
                    rx="8"
                    ry="8"
                    fill="var(--ds-brand-primary-dark)"
                  />
                  <path
                    d="M10.5261 7.99958H5.05243M7.78927 5.47327V10.5259"
                    stroke="white"
                    stroke-width="0.860438"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
              <div class="text-xs mt-2">
                <span
                  class="text-primary cursor-pointer"
                  @click="isShowingPreferredRequestInfoDialog = true"
                >
                  {{ $t('bookings.newReservationCta') }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Main Tab -->
        <VTabs
          v-model="tab"
          class="tab-height"
          align-tabs="center"
          fixed-tabs
        >
          <VTab
            density="comfortable"
            value="Bookings"
            class="tab-height"
          >
            {{ $t('bookings.tab') }}
            <span v-if="pastAndCurrentAcceptedBookings.length">
              ({{ numberWithCommas(pastAndCurrentAcceptedBookings.length) }})
            </span>
          </VTab>
          <VTab
            density="comfortable"
            value="Insights"
            class="tab-height"
          >
            Stats
          </VTab>
        </VTabs>

        <!-- Loading Spinner -->
        <div
          v-if="fetching"
          class="reservations-loading-spinner"
        >
          <VProgressCircular
            indeterminate
            color="primary"
          />
        </div>

        <!-- Bookings Content -->
        <VWindow v-model="tab">
          <VWindowItem
            value="Bookings"
            class="pb-3 mt-2"
          >
            <!-- RM-359: just submitted, RM reviews it before it goes to the restaurant -->
            <ApprovalNoticeBanner class="ma-1 mt-3" />

            <div
              v-if="!fetching && bookingsCount === 0"
              class="upcoming-empty-state content-card ma-1 mt-4"
            >
              <div class="text-sm text-center ma-4">
                <span
                  class="cursor-pointer text-primary"
                  @click="isShowingPreferredRequestInfoDialog = true"
                >
                  {{ $t('bookings.emptyState') }}
                </span>
              </div>
            </div>

            <!-- Reservation Groups -->
            <div
              v-for="tabItem in reservationGroupings"
              :key="tabItem.id"
            >
              <div
                v-if="tabItem.items.length > 0"
                class="mt-1 mb-1 text-sm"
                @click.stop="showingGroups[tabItem.id] = !showingGroups[tabItem.id]"
              >
                <b>
                  {{ tabItem.name }}
                  <span v-if="tabItem.id === 'Experienced'">
                    ({{ tabItem.items.length }})
                  </span>
                  <VBtn
                    density="compact"
                    size="small"
                    class="map-toggle"
                    variant="text"
                    color="secondary"
                    :icon="!showingGroups[tabItem.id] ? 'mdi-chevron-down' : 'mdi-chevron-up'"
                  />
                </b>
              </div>

              <!-- Reservation List -->
              <VExpandTransition>
                <div
                  v-if="showingGroups[tabItem.id]"
                  id="my-reservations"
                  class="flex flex-column pt-0"
                >
                  <div
                    v-if="!fetching && bookingsCount === 0"
                    class="upcoming-empty-state content-card ma-1 mt-4"
                  >
                    <div class="text-sm">
                      {{ $t('bookings.emptyStateInner') }}
                    </div>
                  </div>
                  <div
                    v-else
                    class="bookings-grid-wrapper mt-2"
                  >
                    <div class="bookings-grid">
                      <Booking
                        v-for="item in tabItem.items"
                        :key="item._id"
                        :item="item"
                        :experienced="tabItem.id === 'Experienced'"
                        @new-draft="newDraft"
                        @show-restaurant-details="showRestaurantDetails"
                      />
                    </div>
                  </div>
                </div>
              </VExpandTransition>
            </div>
          </VWindowItem>

          <VWindowItem
            value="Insights"
            class="pb-3 mt-2"
          >
            <BookingInsights />
          </VWindowItem>
        </VWindow>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.main-wrapper {
  width: 100%;
  margin: 0 auto;
  padding: 0 0.4rem;
}

/* Shared ~1160px container, matching Discover + the navbar contents, so
   the list shares the nav's left edge instead of a narrow centered column */
.max-content-width {
  max-width: 1160px;
  padding: 0 12px;
}

@media (min-width: 960px) {
  .max-content-width {
    padding: 0 32px;
  }
}

.tab-height {
  height: 60px !important;
}

.bookings-grid-wrapper {
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

/* Line-separated rows: continuous hairlines (no gaps between rows);
   two columns on desktop like Discover, one on mobile */
.bookings-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
  width: 100%;
  max-width: 100%;
}

@media (min-width: 961px) {
  .bookings-grid {
    /* Prototype: capped column measure, matching Discover */
    grid-template-columns: repeat(2, minmax(0, 520px));
    justify-content: space-between;
    column-gap: 56px;
    row-gap: 0;
  }
}

.upcoming-empty-state {
  min-height: 36vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

:deep(.bookings-grid .booking-container) {
  margin-bottom: 0;
}

.reservations-loading-spinner {
  position: absolute;
  right: 5px;
  top: 20px;
}

.book-now-holder {
  text-align: center;
}

.header-new-reservation-button-sticky-holder {
  overflow: visible;
  position: sticky;
  z-index: 10;
  top: 5.5rem;
}

.google-attribution {
  width: fit-content;
  margin-bottom: 1rem;
  margin-left: 0.4rem;
}

:deep(.v-btn--active) {
  border-bottom: 1px solid;
}

:deep(.v-field__input) {
  font-size: 16px !important;
}

:deep(.v-autocomplete .v-autocomplete__selection-text) {
  overflow: unset !important;
}

:deep(.v-table > .v-table__wrapper > table > tbody > tr > td) {
  padding: 0 !important;
}

:deep(.v-table > .v-table__wrapper > table > thead > tr > th) {
  font-weight: bold;
  padding: 0 !important;
}

:deep(.outer-table > .v-table__wrapper > table) {
  padding-left: 4px !important;
}

:deep(.inner-table > .v-table__wrapper > table) {
  padding-left: 2px !important;
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: AclDemo
  allowLoggedInOnly: true
</route>
